import { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from 'antd';

const { confirm } = Modal;

/**
 * A hook to prompt Modal.confirm with custom behavior. If pendingChanges proceed with modal confirm
 * If !pendingChanges perform the original action
 * @param hasPendingChanges
 * @param shouldAlwaysBeOpen
 * @returns {{showModalConfirm: ((function({defaultAction?: function(), onConfirm?: function(), onMaskClick?: function()}): void))}}
 */
export const useModalConfirm = (hasPendingChanges, shouldAlwaysBeOpen = false) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const modalRef = useRef(null);

  useEffect(() => {
    setIsModalOpen(shouldAlwaysBeOpen || hasPendingChanges);
  }, [hasPendingChanges, shouldAlwaysBeOpen]);

  useEffect(() => {
    if (!isModalOpen && modalRef.current !== null) {
      modalRef?.current?.destroy();
      modalRef.current = null;
    }
  }, [isModalOpen]);

  const showModalConfirm = useCallback(
    ({
      defaultAction = () => {},
      onConfirm = () => {},
      onMaskClick = () => {},
      ...rest
    }) => {
      if(isModalOpen){
        modalRef.current = confirm({
          open: isModalOpen,
          closable: true,
          onOk: async () => {
            try {
              await onConfirm();
            } finally {
              // Always destroy the modal, even if onConfirm rejected, so the
              // user is returned to the page to see validation/server errors.
              modalRef?.current?.destroy();
              modalRef.current = null;
            }
          },
          onCancel: (event) => {

            //Use event name prop to check if it's from maskClosable or Cancel button
            //Empty name indicates mask click or close icon click, so if not empty proceed with defaultAction()
            if (event && event.name?.length > 0) {
              defaultAction();
            } else {
              onMaskClick();
            }
            modalRef?.current?.destroy();
            modalRef.current = null;
          },
          maskClosable: true,
          ...rest
        });
      } else {
        defaultAction();
      }
    }, [isModalOpen, confirm]);

  return { showModalConfirm };
};
